import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {createNewCard, getCustomerCards, removeCustomerCard, setMainCard} from 'api/requests';
import {useAuth, useToast, useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {MaibCardsType, QueryKeyEnum} from 'types';

const useGetCards = () => {
  const {locale} = useRouter();
  const {isAuthenticated} = useAuth();

  return useQuery<MaibCardsType, unknown, MaibCardsType, (string | undefined)[]>({
    queryKey: [QueryKeyEnum.CUSTOMER_CARDS, locale],
    queryFn: () => getCustomerCards(),
    staleTime: 0,
    enabled: isAuthenticated,
  });
};

export const useCreateCard = () => {
  const toast = useToast();
  const router = useRouter();

  return useMutation({
    mutationFn: createNewCard,
    onError: () => {
      toast.error('Error on create card');
    },
    onSuccess: (data) => {
      if (data.url) router.replace(data.url);
    },
  });
};

const useRemoveCard = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const {t} = useTranslation();

  return useMutation({
    mutationFn: removeCustomerCard,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: [QueryKeyEnum.CUSTOMER_CARDS]});
      toast.success(t('CARD_REMOVED'));
    },
    onError: () => {
      toast.error('Error on remove card');
    },
  });
};

const useSetMainCard = () => {
  const toast = useToast();
  const {t} = useTranslation();

  return useMutation({
    mutationFn: setMainCard,
    onSuccess: () => {
      toast.success(t('SUCCESS_UPDATE_DATA'));
    },
    onError: () => {
      toast.error('Error on set main card');
    },
  });
};

export {useSetMainCard, useRemoveCard, useGetCards};
